import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import * as api from '../../../api/index';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Spinner from '../../Atoms/Spinner';

dayjs.locale('fr');

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

// Main Container
const Container = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  overflow: hidden;
`;

// Header with controls
const Header = styled.div`
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
`;

const SearchBar = styled.input`
  padding: 10px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 25px;
  width: 300px;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ExportButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const ColumnToggleButton = styled(Button)`
  background: white;
  border: 2px solid #e0e0e0;
  color: #333;
  
  &:hover {
    border-color: #667eea;
  }
`;

// Grid Container
const GridContainer = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 20px;
  position: relative;
`;

const GridWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
    
    &:hover {
      background: #555;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  min-width: 2000px;
`;

const TableHeader = styled.thead`
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderRow = styled.tr``;

const HeaderCell = styled.th`
  padding: 12px 15px;
  text-align: left;
  color: ${props => props.isPinned ? 'white' : '#333'};
  font-weight: 600;
  border-right: 1px solid ${props => props.isPinned ? 'rgba(255, 255, 255, 0.2)' : '#e0e0e0'};
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  user-select: none;
  position: ${props => props.isPinned ? 'sticky' : 'relative'};
  left: ${props => props.isPinned ? props.leftOffset : 'auto'};
  z-index: ${props => props.isPinned ? '11' : '10'};
  white-space: nowrap;
  min-width: ${props => props.width || 'auto'};
  width: ${props => props.width || 'auto'};
  background: ${props => props.isPinned 
    ? '#9333ea' 
    : '#f8f9fa'};
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    ${props => props.sortable && `
      background: ${props.isPinned 
        ? '#a855f7'
        : '#e9ecef'};
    `}
  }
  
  .sort-indicator {
    display: inline-block;
    margin-left: 8px;
    font-size: 12px;
    opacity: ${props => props.isActive ? '1' : '0.5'};
  }
  
  .resize-handle {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    cursor: col-resize;
    background: transparent;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  transition: background-color 0.2s ease;
  
  &:nth-child(even) {
    background-color: #f8f9fa;
  }
  
  &:hover {
    background-color: #e8f4ff;
  }
`;

const TableCell = styled.td`
  padding: 10px 12px;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  position: ${props => props.isPinned ? 'sticky' : 'relative'};
  left: ${props => props.isPinned ? props.leftOffset : 'auto'};
  z-index: ${props => props.isPinned ? '1' : 'auto'};
  min-width: ${props => props.width || 'auto'};
  width: ${props => props.width || 'auto'};
  max-width: ${props => props.width || 'none'};
  overflow: ${props => props.allowOverflow ? 'visible' : 'hidden'};
  text-overflow: ellipsis;
  background: ${props => props.isPinned ? '#f8f9ff' : 'inherit'};

  &:last-child {
    border-right: none;
  }
`;

const EditableCell = styled.div`
  min-height: 20px;
  cursor: text;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  white-space: normal;
  word-wrap: break-word;
  max-width: 100%;
  
  &:hover {
    background: #f0f0f0;
  }
  
  &.editing {
    background: transparent;
    padding: 0;
  }
`;

const CellInput = styled.input`
  width: 100%;
  padding: 4px 8px;
  border: 2px solid #667eea;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  background: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch(props.status) {
      case 'pending':
        return 'background: #ffc107; color: #000;';
      case 'reviewed':
        return 'background: #17a2b8; color: white;';
      case 'approved':
        return 'background: #28a745; color: white;';
      case 'rejected':
        return 'background: #dc3545; color: white;';
      default:
        return 'background: #6c757d; color: white;';
    }
  }}

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

const StatusContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const StatusDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 140px;
  overflow: hidden;
  white-space: nowrap;
`;

const StatusOption = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }

  &.selected {
    background-color: #e8f4ff;
    font-weight: 600;
  }
`;

// Image Cell Components
const ImageCellContainer = styled.div`
  position: relative;
  width: 100%;
  height: 80px;
  overflow: hidden;
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-color: #f0f0f0;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    opacity: 0.95;
  }
`;

const ImageNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border: none;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.2s ease;
  z-index: 2;
  padding: 0;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
  
  &:disabled {
    background: rgba(0, 0, 0, 0.3);
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  &.prev {
    left: 3px;
  }
  
  &.next {
    right: 3px;
  }
`;

const MediaCount = styled.span`
  position: absolute;
  bottom: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  z-index: 2;
`;

// Toast Notification
const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1000;
  animation: ${props => props.isClosing ? fadeOut : fadeIn} 0.3s ease;
  
  &::before {
    content: '✓';
    width: 24px;
    height: 24px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// Column Manager Dropdown
const ColumnManagerDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 15px;
  margin-top: 10px;
  z-index: 200;
  min-width: 250px;
`;

const ColumnItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const ColumnCheckbox = styled.input`
  margin-right: 10px;
  cursor: pointer;
`;

const AddCommentButton = styled.button`
  padding: 4px 8px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #764ba2;
  }
`;

const DeleteCommentButton = styled.button`
  padding: 4px 8px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 5px;
  
  &:hover {
    background: #c82333;
  }
`;

const PinButton = styled.button`
  padding: 4px 8px;
  background: ${props => props.isPinned ? '#ffc107' : '#6c757d'};
  color: ${props => props.isPinned ? '#000' : '#fff'};
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 5px;
  
  &:hover {
    background: ${props => props.isPinned ? '#ffb300' : '#5a6268'};
  }
`;

// Confirmation Dialog
const ConfirmDialog = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  min-width: 400px;
  animation: ${fadeIn} 0.3s ease;
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
`;

const DialogTitle = styled.h3`
  margin: 0 0 15px 0;
  font-size: 20px;
  color: #333;
`;

const DialogMessage = styled.p`
  margin: 0 0 25px 0;
  font-size: 15px;
  color: #666;
  line-height: 1.5;
`;

const DialogButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const DialogButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.cancel {
    background: #e0e0e0;
    color: #333;
    
    &:hover {
      background: #d0d0d0;
    }
  }
  
  &.confirm {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
`;

// Lightbox Modal
const LightboxModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
`;

const LightboxImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

// Loading Component
const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  flex-direction: column;
  gap: 20px;
`;

const LoadingText = styled.p`
  font-size: 18px;
  color: #666;
`;

// Main Component
const TableauDemo = ({ fullUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [imageIndices, setImageIndices] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  
  // Column configuration with all Milan 2026 fields
  const [columns, setColumns] = useState([
    { id: 'date', label: 'Date', visible: true, editable: true, sortable: true, width: '120px', pinned: false },
    { id: 'userType', label: 'Type', visible: true, editable: true, sortable: true, width: '100px', pinned: false },
    { id: 'company', label: 'Entreprise', visible: true, editable: true, sortable: true, width: '150px', pinned: false },
    { id: 'contact', label: 'Contact', visible: true, editable: true, sortable: true, width: '150px', pinned: false },
    { id: 'email', label: 'Email', visible: true, editable: true, sortable: true, width: '180px', pinned: false },
    { id: 'phoneNumber', label: 'Téléphone', visible: true, editable: true, sortable: true, width: '120px', pinned: false },

    // Designer fields
    { id: 'designerInternationalPresence', label: 'Designer - Présence internationale', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'designerInternationalDetails', label: 'Designer - Détails international', visible: false, editable: true, sortable: true, width: '250px', pinned: false },
    { id: 'designerWebAndSocial', label: 'Designer - Web & réseaux sociaux', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'designerWebAndSocialDetails', label: 'Designer - Détails web/social', visible: false, editable: true, sortable: true, width: '250px', pinned: false },
    { id: 'designerPresentation', label: 'Designer - Présentation', visible: false, editable: true, sortable: true, width: '300px', pinned: false },

    // Fabricant fields
    { id: 'fabricantCodifab', label: 'Fabricant - Cotisant CODIFAB', visible: false, editable: true, sortable: true, width: '150px', pinned: false },
    { id: 'fabricantAmeublement', label: 'Fabricant - Adhérent Ameublement français', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'fabricantInternationalPresence', label: 'Fabricant - Présence internationale', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'fabricantInternationalDetails', label: 'Fabricant - Détails international', visible: false, editable: true, sortable: true, width: '250px', pinned: false },
    { id: 'fabricantWebAndSocial', label: 'Fabricant - Web & réseaux sociaux', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'fabricantWebAndSocialDetails', label: 'Fabricant - Détails web/social', visible: false, editable: true, sortable: true, width: '250px', pinned: false },
    { id: 'fabricantPresentation', label: 'Fabricant - Présentation', visible: false, editable: true, sortable: true, width: '300px', pinned: false },

    // Piece fields
    { id: 'pieceName', label: 'Nom de la pièce', visible: true, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'pieceTypologie', label: 'Typologie', visible: true, editable: true, sortable: true, width: '150px', pinned: false },
    { id: 'pieceCreationDate', label: 'Date de création', visible: true, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'pieceSavoirFaire', label: 'Savoir-faire', visible: true, editable: true, sortable: true, width: '250px', pinned: false },
    { id: 'pieceDurability', label: 'Durabilité', visible: true, editable: true, sortable: true, width: '300px', pinned: false },
    { id: 'piecePresentationText', label: 'Présentation de la pièce', visible: true, editable: true, sortable: true, width: '350px', pinned: false },
    { id: 'pieceDimensions', label: 'Dimensions', visible: true, editable: true, sortable: true, width: '150px', pinned: false },
    { id: 'pieceMaterials', label: 'Matériaux', visible: true, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'piecePoids', label: 'Poids', visible: false, editable: true, sortable: true, width: '100px', pinned: false },

    // Piece designer info (for fabricant submissions)
    { id: 'pieceDesignerCompanyName', label: 'Pièce - Designer entreprise', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'pieceDesignerInternationalPresence', label: 'Pièce - Designer présence int.', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'pieceDesignerInternationalDetails', label: 'Pièce - Designer détails int.', visible: false, editable: true, sortable: true, width: '250px', pinned: false },
    { id: 'pieceDesignerWebAndSocial', label: 'Pièce - Designer web/social', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'pieceDesignerWebAndSocialDetails', label: 'Pièce - Designer détails web/social', visible: false, editable: true, sortable: true, width: '250px', pinned: false },
    { id: 'pieceDesignerPresentation', label: 'Pièce - Designer présentation', visible: false, editable: true, sortable: true, width: '300px', pinned: false },

    // Piece fabricant info (for designer submissions)
    { id: 'pieceFabricantCompanyName', label: 'Pièce - Fabricant entreprise', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'pieceFabricantCodifab', label: 'Pièce - Fabricant CODIFAB', visible: false, editable: true, sortable: true, width: '150px', pinned: false },
    { id: 'pieceFabricantAmeublement', label: 'Pièce - Fabricant Ameublement', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'pieceFabricantInternationalPresence', label: 'Pièce - Fabricant présence int.', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'pieceFabricantInternationalDetails', label: 'Pièce - Fabricant détails int.', visible: false, editable: true, sortable: true, width: '250px', pinned: false },
    { id: 'pieceFabricantWebAndSocial', label: 'Pièce - Fabricant web/social', visible: false, editable: true, sortable: true, width: '200px', pinned: false },
    { id: 'pieceFabricantWebAndSocialDetails', label: 'Pièce - Fabricant détails web/social', visible: false, editable: true, sortable: true, width: '250px', pinned: false },
    { id: 'pieceFabricantPresentation', label: 'Pièce - Fabricant présentation', visible: false, editable: true, sortable: true, width: '300px', pinned: false },

    { id: 'media', label: 'Médias', visible: true, editable: false, sortable: false, width: '120px', pinned: false },
    { id: 'status', label: 'Statut', visible: true, editable: true, sortable: true, width: '140px', pinned: false },
    { id: 'actions', label: 'Actions', visible: true, editable: false, sortable: false, width: '100px', pinned: false },
  ]);
  
  const [commentColumns, setCommentColumns] = useState({});

  useEffect(() => {
    // Admin check disabled for demo
    // if (!fullUser || fullUser?.user?.role !== 'admin') {
    //   navigate('/');
    //   return;
    // }
    
    fetchData();
  }, [fullUser, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.status-container')) {
        setEditingStatus(null);
      }
    };

    if (editingStatus) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.getFormSubmissions({ formName: 'milan-2026' });
      const submissionsData = response.data.submissions || [];
      
      // Transform data to flat structure for grid with all Milan 2026 fields
      const flatData = [];
      submissionsData.forEach(submission => {
        if (submission.data.pieces && submission.data.pieces.length > 0) {
          submission.data.pieces.forEach((piece, index) => {
            flatData.push({
              id: `${submission._id}-${index}`,
              submissionId: submission._id,
              date: submission.createdAt,
              userType: submission.data.userType === 'designer' ? 'Designer' : 'Fabricant',
              company: submission.data.userType === 'designer' 
                ? submission.data.designerCompanyName 
                : submission.data.fabricantCompanyName,
              contact: `${submission.data.firstName} ${submission.data.lastName}`,
              email: submission.data.email,
              phone: submission.data.phone || '',
              pieceName: piece.name || '',
              pieceDescription: piece.description || '',
              pieceCategory: piece.category || '',
              pieceMaterials: piece.materials || '',
              pieceDimensions: piece.dimensions || '',
              piecePrice: piece.price || '',
              media: piece.uploadedMedia || [],
              status: piece.status || 'pending',  // Use piece-level status
              originalData: submission.data
            });
          });
        }
      });
      
      setData(flatData);
      setFilteredData(flatData);
      
      // Initialize image indices
      const indices = {};
      flatData.forEach(row => {
        if (row.media && row.media.length > 0) {
          indices[row.id] = 0;
        }
      });
      setImageIndices(indices);
    } catch (err) {
      console.error('Error fetching data:', err);
      showToast('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message) => {
    setToast({ message, isClosing: false });
    setTimeout(() => {
      setToast(prev => ({ ...prev, isClosing: true }));
      setTimeout(() => setToast(null), 300);
    }, 2000);
  };

  const handleCellEdit = (rowId, columnId, value) => {
    setData(prev => prev.map(row =>
      row.id === rowId ? { ...row, [columnId]: value } : row
    ));
    setFilteredData(prev => prev.map(row =>
      row.id === rowId ? { ...row, [columnId]: value } : row
    ));
    showToast('Données enregistrées');
    setEditingCell(null);
  };

  const handleStatusChange = async (rowId, newStatus) => {
    try {
      // Get the row to find submission and piece info
      const row = data.find(r => r.id === rowId);
      if (!row) return;

      const [submissionId, pieceIndexStr] = row.id.split('-');
      const pieceIndex = parseInt(pieceIndexStr);

      // Update the status via API using piece-level endpoint
      await api.updatePieceStatus(submissionId, pieceIndex, newStatus);

      // Update local state
      setData(prev => prev.map(r =>
        r.id === rowId ? { ...r, status: newStatus } : r
      ));
      setFilteredData(prev => prev.map(r =>
        r.id === rowId ? { ...r, status: newStatus } : r
      ));

      showToast('Statut mis à jour avec succès');
      setEditingStatus(null);
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('Erreur lors de la mise à jour du statut');
      setEditingStatus(null);
    }
  };

  const handleCommentEdit = (rowId, columnId, value) => {
    setCommentColumns(prev => ({
      ...prev,
      [`${rowId}-${columnId}`]: value
    }));
    showToast('Commentaire enregistré');
    setEditingCell(null);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (term === '') {
      setFilteredData(data);
    } else {
      const filtered = data.filter(row => 
        Object.values(row).some(value => 
          value && value.toString().toLowerCase().includes(term)
        )
      );
      setFilteredData(filtered);
    }
  };

  const handleSort = (columnId) => {
    const column = columns.find(c => c.id === columnId);
    if (!column.sortable) return;
    
    let direction = 'asc';
    if (sortConfig.key === columnId && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    const sorted = [...filteredData].sort((a, b) => {
      if (a[columnId] < b[columnId]) return direction === 'asc' ? -1 : 1;
      if (a[columnId] > b[columnId]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredData(sorted);
    setSortConfig({ key: columnId, direction });
  };

  const handleImageNav = (rowId, direction) => {
    setImageIndices(prev => {
      const currentIndex = prev[rowId] || 0;
      const row = filteredData.find(r => r.id === rowId);
      const maxIndex = row.media.length - 1;
      
      let newIndex = currentIndex;
      if (direction === 'next' && currentIndex < maxIndex) {
        newIndex = currentIndex + 1;
      } else if (direction === 'prev' && currentIndex > 0) {
        newIndex = currentIndex - 1;
      }
      
      return { ...prev, [rowId]: newIndex };
    });
  };

  const toggleColumn = (columnId) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  };

  const togglePinColumn = (columnId) => {
    setColumns(prev => prev.map(col => 
      col.id === columnId ? { ...col, pinned: !col.pinned } : col
    ));
  };

  const addCommentColumn = (columnId) => {
    const column = columns.find(c => c.id === columnId);
    const columnIndex = columns.findIndex(c => c.id === columnId);
    
    const commentColumn = {
      id: `${columnId}_comment`,
      label: `${column.label} (Commentaire)`,
      visible: true,
      editable: true,
      sortable: false,
      isComment: true,
      parentColumn: columnId,
      width: '200px'
    };
    
    setColumns(prev => [
      ...prev.slice(0, columnIndex + 1),
      commentColumn,
      ...prev.slice(columnIndex + 1)
    ]);
    
    setShowColumnManager(false);
  };

  const countNonEmptyComments = (columnId) => {
    let count = 0;
    filteredData.forEach(row => {
      const commentKey = `${row.id}-${columnId.replace('_comment', '')}`;
      if (commentColumns[commentKey] && commentColumns[commentKey].trim() !== '') {
        count++;
      }
    });
    return count;
  };

  const deleteCommentColumn = (columnId) => {
    const nonEmptyCount = countNonEmptyComments(columnId);
    
    if (nonEmptyCount > 0) {
      setDeleteConfirm({
        columnId,
        nonEmptyCount
      });
    } else {
      // Delete immediately if no data
      performDeleteColumn(columnId);
    }
  };

  const performDeleteColumn = (columnId) => {
    // Remove column from columns array
    setColumns(prev => prev.filter(col => col.id !== columnId));
    
    // Clean up comment data for this column
    const parentColumn = columnId.replace('_comment', '');
    const newCommentColumns = { ...commentColumns };
    filteredData.forEach(row => {
      const commentKey = `${row.id}-${parentColumn}`;
      delete newCommentColumns[commentKey];
    });
    setCommentColumns(newCommentColumns);
    
    setDeleteConfirm(null);
    showToast('Colonne supprimée');
  };

  const handleExport = (type) => {
    showToast(`Export ${type} - Fonctionnalité à venir`);
  };

  const handleColumnResize = (columnId, event) => {
    const startX = event.pageX;
    const column = columns.find(c => c.id === columnId);
    const startWidth = parseInt(column.width) || 150;
    
    const handleMouseMove = (e) => {
      const diff = e.pageX - startX;
      const newWidth = Math.max(50, startWidth + diff);
      
      setColumns(prev => prev.map(col => 
        col.id === columnId 
          ? { ...col, width: `${newWidth}px` }
          : col
      ));
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(columns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setColumns(items);
  };

  const getMediaUrl = (media) => {
    const baseUrl = process.env.REACT_APP_MEDIA_BASE_URL || 'http://localhost:4000';
    return `${baseUrl}${media.path}`;
  };

  // Calculate left offset for pinned columns
  const calculatePinnedOffsets = () => {
    const visibleColumns = columns.filter(col => col.visible);
    const offsets = {};
    let currentOffset = 0;
    
    visibleColumns.forEach(col => {
      if (col.pinned) {
        offsets[col.id] = `${currentOffset}px`;
        currentOffset += parseInt(col.width) || 150;
      }
    });
    
    return offsets;
  };
  
  const pinnedOffsets = calculatePinnedOffsets();

  const renderCell = (row, column) => {
    const cellId = `${row.id}-${column.id}`;
    const isEditing = editingCell === cellId;
    
    if (column.isComment) {
      const value = commentColumns[`${row.id}-${column.parentColumn}`] || '';
      
      if (isEditing) {
        return (
          <CellInput
            autoFocus
            value={value}
            onChange={(e) => setCommentColumns(prev => ({
              ...prev,
              [`${row.id}-${column.parentColumn}`]: e.target.value
            }))}
            onBlur={() => {
              handleCommentEdit(row.id, column.parentColumn, value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCommentEdit(row.id, column.parentColumn, value);
              } else if (e.key === 'Escape') {
                setEditingCell(null);
              }
            }}
          />
        );
      }
      
      return (
        <EditableCell
          onClick={() => setEditingCell(cellId)}
          style={{ fontStyle: value ? 'normal' : 'italic', color: value ? '#333' : '#999' }}
        >
          {value || 'Ajouter un commentaire...'}
        </EditableCell>
      );
    }
    
    switch (column.id) {
      case 'date':
        const formattedDate = dayjs(row.date).format('DD/MM/YYYY HH:mm');
        if (isEditing) {
          return (
            <CellInput
              autoFocus
              value={formattedDate}
              onChange={(e) => {}}
              onBlur={() => handleCellEdit(row.id, column.id, formattedDate)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCellEdit(row.id, column.id, formattedDate);
                } else if (e.key === 'Escape') {
                  setEditingCell(null);
                }
              }}
            />
          );
        }
        return (
          <EditableCell onClick={() => column.editable && setEditingCell(cellId)}>
            {formattedDate}
          </EditableCell>
        );
        
      case 'media':
        if (!row.media || row.media.length === 0) {
          return (
            <ImageCellContainer>
              <ImagePreview style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                Aucun média
              </ImagePreview>
            </ImageCellContainer>
          );
        }
        
        const currentIndex = imageIndices[row.id] || 0;
        const currentMedia = row.media[currentIndex];
        
        return (
          <ImageCellContainer>
            <ImagePreview
              style={{ backgroundImage: `url(${getMediaUrl(currentMedia)})` }}
              onClick={() => setLightboxImage(getMediaUrl(currentMedia))}
            />
            {row.media.length > 1 && (
              <>
                <ImageNavButton
                  className="prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageNav(row.id, 'prev');
                  }}
                  disabled={currentIndex === 0}
                >
                  ‹
                </ImageNavButton>
                <ImageNavButton
                  className="next"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageNav(row.id, 'next');
                  }}
                  disabled={currentIndex === row.media.length - 1}
                >
                  ›
                </ImageNavButton>
              </>
            )}
            <MediaCount>{currentIndex + 1}/{row.media.length}</MediaCount>
          </ImageCellContainer>
        );
        
      case 'status':
        const getStatusLabel = (status) => {
          switch(status) {
            case 'pending': return 'À valider';
            case 'reviewed': return 'Examiné';
            case 'approved': return 'Validé';
            case 'rejected': return 'Refusé';
            default: return status;
          }
        };

        return (
          <StatusContainer className="status-container">
            <StatusBadge
              status={row.status}
              onClick={() => setEditingStatus(row.id)}
            >
              {getStatusLabel(row.status)}
            </StatusBadge>
            {editingStatus === row.id && (
              <StatusDropdown>
                <StatusOption
                  className={row.status === 'pending' ? 'selected' : ''}
                  onClick={() => handleStatusChange(row.id, 'pending')}
                >
                  À valider
                </StatusOption>
                <StatusOption
                  className={row.status === 'reviewed' ? 'selected' : ''}
                  onClick={() => handleStatusChange(row.id, 'reviewed')}
                >
                  Examiné
                </StatusOption>
                <StatusOption
                  className={row.status === 'approved' ? 'selected' : ''}
                  onClick={() => handleStatusChange(row.id, 'approved')}
                >
                  Validé
                </StatusOption>
                <StatusOption
                  className={row.status === 'rejected' ? 'selected' : ''}
                  onClick={() => handleStatusChange(row.id, 'rejected')}
                >
                  Refusé
                </StatusOption>
              </StatusDropdown>
            )}
          </StatusContainer>
        );
        
      case 'actions':
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              style={{ 
                padding: '6px 12px', 
                fontSize: '12px',
                background: '#667eea',
                color: 'white'
              }}
              onClick={() => navigate(`/admin/forms/submission/${row.submissionId}`)}
            >
              Voir
            </Button>
          </div>
        );
        
      default:
        const value = row[column.id] || '';
        if (isEditing) {
          return (
            <CellInput
              autoFocus
              value={value}
              onChange={(e) => {
                const newData = [...filteredData];
                const rowIndex = newData.findIndex(r => r.id === row.id);
                newData[rowIndex][column.id] = e.target.value;
                setFilteredData(newData);
              }}
              onBlur={() => handleCellEdit(row.id, column.id, value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCellEdit(row.id, column.id, value);
                } else if (e.key === 'Escape') {
                  setEditingCell(null);
                }
              }}
            />
          );
        }
        return (
          <EditableCell 
            onClick={() => column.editable && setEditingCell(cellId)}
            className={isEditing ? 'editing' : ''}
          >
            {value}
          </EditableCell>
        );
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
        <LoadingText>Chargement des données...</LoadingText>
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Tableau Milan 2026</Title>
        <Controls>
          <SearchBar
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={handleSearch}
          />
          <div style={{ position: 'relative' }}>
            <ColumnToggleButton onClick={() => setShowColumnManager(!showColumnManager)}>
              ⚙️ Colonnes
            </ColumnToggleButton>
            {showColumnManager && (
              <ColumnManagerDropdown>
                <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>Gérer les colonnes</h4>
                {columns.map(column => (
                  <ColumnItem key={column.id}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <ColumnCheckbox
                        type="checkbox"
                        checked={column.visible}
                        onChange={() => toggleColumn(column.id)}
                      />
                      <span style={{ fontSize: '13px', color: column.isComment ? '#666' : '#333' }}>
                        {column.label}
                        {column.pinned && ' 📌'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {!column.isComment && (
                        <PinButton 
                          isPinned={column.pinned}
                          onClick={() => togglePinColumn(column.id)}
                          title={column.pinned ? 'Détacher' : 'Épingler'}
                        >
                          📌
                        </PinButton>
                      )}
                      {!column.isComment && column.id !== 'media' && column.id !== 'actions' && !columns.find(c => c.parentColumn === column.id) && (
                        <AddCommentButton onClick={() => addCommentColumn(column.id)}>
                          + Com.
                        </AddCommentButton>
                      )}
                      {column.isComment && (
                        <DeleteCommentButton onClick={() => deleteCommentColumn(column.id)}>
                          Suppr.
                        </DeleteCommentButton>
                      )}
                    </div>
                  </ColumnItem>
                ))}
              </ColumnManagerDropdown>
            )}
          </div>
          <ExportButton onClick={() => handleExport('Excel')}>
            📊 Export Excel
          </ExportButton>
          <ExportButton onClick={() => handleExport('PDF')}>
            📄 Export PDF
          </ExportButton>
        </Controls>
      </Header>
      
      <GridContainer>
        <GridWrapper>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="columns" direction="horizontal">
              {(provided) => (
                <TableContainer>
                  <Table>
                    <TableHeader>
                      <HeaderRow ref={provided.innerRef} {...provided.droppableProps}>
                        {columns.filter(col => col.visible).map((column, index) => (
                          <Draggable key={column.id} draggableId={column.id} index={index}>
                            {(provided, snapshot) => (
                              <HeaderCell
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sortable={column.sortable}
                                width={column.width}
                                isPinned={column.pinned}
                                leftOffset={pinnedOffsets[column.id]}
                                onClick={(e) => {
                                  // Don't sort if clicking on resize handle
                                  if (!e.target.classList.contains('resize-handle')) {
                                    handleSort(column.id);
                                  }
                                }}
                                style={{
                                  ...provided.draggableProps.style,
                                  cursor: snapshot.isDragging ? 'grabbing' : 'pointer'
                                }}
                              >
                                <div {...provided.dragHandleProps}>
                                  {column.label}
                                  {column.sortable && (
                                    <span className="sort-indicator" isActive={sortConfig.key === column.id}>
                                      {sortConfig.key === column.id 
                                        ? (sortConfig.direction === 'asc' ? '↑' : '↓')
                                        : '↕'
                                      }
                                    </span>
                                  )}
                                </div>
                                <div 
                                  className="resize-handle" 
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleColumnResize(column.id, e);
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}
                                />
                              </HeaderCell>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </HeaderRow>
                    </TableHeader>
                    <TableBody>
                      {filteredData.map(row => (
                        <TableRow key={row.id}>
                          {columns.filter(col => col.visible).map(column => (
                            <TableCell
                              key={`${row.id}-${column.id}`}
                              width={column.width}
                              isPinned={column.pinned}
                              leftOffset={pinnedOffsets[column.id]}
                              allowOverflow={column.id === 'status'}
                              style={column.id === 'media' ? { padding: 0 } : {}}
                            >
                              {renderCell(row, column)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Droppable>
          </DragDropContext>
        </GridWrapper>
      </GridContainer>
      
      {toast && (
        <Toast isClosing={toast.isClosing}>
          {toast.message}
        </Toast>
      )}
      
      {lightboxImage && (
        <LightboxModal onClick={() => setLightboxImage(null)}>
          <LightboxClose onClick={() => setLightboxImage(null)}>×</LightboxClose>
          <LightboxImage src={lightboxImage} />
        </LightboxModal>
      )}
      
      {deleteConfirm && (
        <>
          <DialogOverlay onClick={() => setDeleteConfirm(null)} />
          <ConfirmDialog>
            <DialogTitle>Supprimer cette colonne de commentaires ?</DialogTitle>
            <DialogMessage>
              {deleteConfirm.nonEmptyCount > 0 ? (
                <>
                  <strong>Attention :</strong> Cette colonne contient {deleteConfirm.nonEmptyCount} commentaire{deleteConfirm.nonEmptyCount > 1 ? 's' : ''} non vide{deleteConfirm.nonEmptyCount > 1 ? 's' : ''}.
                  <br /><br />
                  Ces données seront définitivement perdues.
                </>
              ) : (
                'Cette colonne ne contient aucun commentaire. Elle peut être supprimée sans perte de données.'
              )}
            </DialogMessage>
            <DialogButtons>
              <DialogButton className="cancel" onClick={() => setDeleteConfirm(null)}>
                Annuler
              </DialogButton>
              <DialogButton 
                className="confirm" 
                onClick={() => performDeleteColumn(deleteConfirm.columnId)}
              >
                Supprimer
              </DialogButton>
            </DialogButtons>
          </ConfirmDialog>
        </>
      )}
    </Container>
  );
};

export default TableauDemo;